import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "./CustomButton";
import Loader from "../dist/Loader";
import { formatDate } from "../utils/formatDate";
import XLSX from "xlsx-js-style";

const SobFY = () => {
  const [states, setStates] = useState([{ id: "All", name: "All" }]);
  const [divisions, setDivisions] = useState([{ id: "All", name: "All" }]);
  const [employees, setEmployees] = useState([
    { id: "All", first_name: "All", last_name: "" },
  ]);

  const [selectedState, setSelectedState] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");

  const getDefaultFinancialYear = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    return currentMonth >= 4
      ? `${currentYear}-${currentYear + 1}`
      : `${currentYear - 1}-${currentYear}`;
  };

  const [financialYear, setFinancialYear] = useState(getDefaultFinancialYear());
  const [financialYearOptions, setFinancialYearOptions] = useState([]);

  const [tableData, setTableData] = useState([]);
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateFinancialYears = () => {
    const startYear = 2024;
    const today = new Date();
    const currentYear = today.getFullYear();
    const fyList = [];
    for (let year = startYear; year <= currentYear; year++) {
      fyList.push(`${year}-${year + 1}`);
    }
    setFinancialYearOptions(fyList);
  };

  useEffect(() => {
    generateFinancialYears();
  }, []);

  const fetchEmployees = useCallback(async (stateId, divisionName) => {
    try {
      let url = `${BASE_URL}/api/employees`;
      const params = {};
      if (stateId && stateId !== "All") params.state_id = stateId;
      if (divisionName && divisionName !== "All")
        params.division = divisionName;
      const res = await axios.get(url, {
        params: Object.keys(params).length ? params : undefined,
        headers: getAuthHeaders(),
      });
      setEmployees([
        { id: "All", first_name: "All", last_name: "" },
        ...res.data,
      ]);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
    }
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        setStates([{ id: "All", name: "All" }, ...response.data]);
      } catch (err) {
        console.error("Error fetching states:", err);
        setError("Failed to load states");
        setStates([{ id: "All", name: "All" }]);
      }
    };
    fetchStates();
  }, []);

  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("All");

    try {
      let url = `${BASE_URL}/api/divisions`;
      if (stateId && stateId !== "All") {
        url += `?state_id=${stateId}`;
      }
      const divisionRes = await axios.get(url, { headers: getAuthHeaders() });
      setDivisions([{ id: "All", name: "All" }, ...divisionRes.data]);
    } catch (err) {
      console.error("Error loading divisions", err);
      setDivisions([{ id: "All", name: "All" }]);
    }

    fetchEmployees(stateId, "All");
  };

  const handleDivisionChange = async (divisionId) => {
    setSelectedDivision(divisionId);
    const division = divisions.find((d) => d.id == divisionId);
    const divisionName = division?.name || "All";
    fetchEmployees(selectedState, divisionName);
  };

  const handleGoClick = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params = {};
      if (selectedState && selectedState !== "All")
        params.state_id = selectedState;
      if (selectedDivision && selectedDivision !== "All") {
        const division = divisions.find((d) => d.id == selectedDivision);
        if (division?.name) params.division = division.name;
      }
      if (selectedEmployee && selectedEmployee !== "All")
        params.employee_id = selectedEmployee;
      if (financialYear) params.financial_year = financialYear;

      const response = await axios.get(
        `${BASE_URL}/api/sob-targets/employee_target_report_sob_fy`,
        {
          params: Object.keys(params).length ? params : undefined,
          headers: getAuthHeaders(),
        }
      );

      setTableData(response.data || []);
      setShowContainers(true);
    } catch (err) {
      console.error("Failed to fetch data", err);
      if (err.response) {
        if (err.response.status === 422) {
          setError(
            `Validation error: ${JSON.stringify(err.response.data.detail)}`
          );
        } else {
          setError(
            `Server error: ${err.response.status} - ${err.response.statusText}`
          );
        }
      } else if (err.request) {
        setError("No response received from server");
      } else {
        setError(`Request error: ${err.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const calculateRunningTotal = (row) => {
    const months = [
      "apr_sob", "may_sob", "jun_sob", "jul_sob", "aug_sob", "sep_sob",
      "oct_sob", "nov_sob", "dec_sob", "jan_sob", "feb_sob", "mar_sob",
    ];
    return months.reduce(
      (total, month) => total + (parseFloat(row[month]) || 0),
      0
    );
  };

  const monthKeys = [
    "apr_sob", "may_sob", "jun_sob", "jul_sob", "aug_sob", "sep_sob",
    "oct_sob", "nov_sob", "dec_sob", "jan_sob", "feb_sob", "mar_sob",
  ];

  const columnTotals = monthKeys.map((key) =>
    tableData.reduce((sum, row) => sum + (parseFloat(row[key]) || 0), 0)
  );

  const grandTotal = columnTotals.reduce((a, b) => a + b, 0);

  const monthsDisplay = [
    "Apr", "May", "Jun", "Jul", "Aug", "Sep",
    "Oct", "Nov", "Dec", "Jan", "Feb", "Mar",
  ];

  // Print handler
  const handlePrint = () => {
    const headerCols = [
      "S.No", "Emp ID", "State", "Employee", "Designation", "HQ", "Division",
      "DOJ", "DOR", "Manager", "Phone No",
      ...monthsDisplay.map((m) => `${m} SOB`),
      "Total",
    ];

    const rowsHtml =
      tableData && tableData.length
        ? tableData
            .map((row, idx) => {
              const running = calculateRunningTotal(row);
              const monthCols = [
                row.apr_sob || 0, row.may_sob || 0, row.jun_sob || 0,
                row.jul_sob || 0, row.aug_sob || 0, row.sep_sob || 0,
                row.oct_sob || 0, row.nov_sob || 0, row.dec_sob || 0,
                row.jan_sob || 0, row.feb_sob || 0, row.mar_sob || 0,
              ]
                .map(
                  (v) =>
                    `<td style="padding:6px;border:1px solid #ccc;text-align:right">${v}</td>`
                )
                .join("");

              return `<tr>
                <td style="padding:6px;border:1px solid #ccc;text-align:center">${idx + 1}</td>
                <td style="padding:6px;border:1px solid #ccc">${row.employee_id || ""}</td>
                <td style="padding:6px;border:1px solid #ccc">${row.state || ""}</td>
                <td style="padding:6px;border:1px solid #ccc">${row.employee_name || ""}</td>
                <td style="padding:6px;border:1px solid #ccc">${row.designation || ""}</td>
                <td style="padding:6px;border:1px solid #ccc">${row.hq || ""}</td>
                <td style="padding:6px;border:1px solid #ccc">${row.division || ""}</td>
                <td style="padding:6px;border:1px solid #ccc">${row.final_joining_date || "-"}</td>
                <td style="padding:6px;border:1px solid #ccc">${row.resign_date || "-"}</td>
                <td style="padding:6px;border:1px solid #ccc">${row.manager || ""}</td>
                <td style="padding:6px;border:1px solid #ccc">${row.phone_no || ""}</td>
                ${monthCols}
                <td style="padding:6px;border:1px solid #ccc;text-align:right">${running.toFixed(1)}</td>
              </tr>`;
            })
            .join("")
        : `<tr><td colspan="${headerCols.length}" style="padding:12px;text-align:center">No data found</td></tr>`;

    // Footer row for print
    const footerMonthCols = columnTotals
      .map(
        (v) =>
          `<td style="padding:6px;border:1px solid #ccc;text-align:right;font-weight:bold">${Math.round(v)}</td>`
      )
      .join("");

    const footerRow = `<tr style="background:#2563eb;color:#fff;font-weight:bold">
      <td colspan="11" style="padding:6px;border:1px solid #ccc;text-align:center">Total</td>
      ${footerMonthCols}
      <td style="padding:6px;border:1px solid #ccc;text-align:right">${Math.round(grandTotal)}</td>
    </tr>`;

    const printWindow = window.open("", "_blank", "width=1200,height=800");
    if (!printWindow) {
      toast.warning("Please allow popups to print.");
      return;
    }

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8"/>
          <title>SOB FY Report - ${financialYear || "All"}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial; padding:16px; }
            table { width:100%; border-collapse: collapse; font-size:12px; }
            th, td { border:1px solid #ccc; padding:6px; vertical-align: top; }
            thead th { background:#f2f7fb; font-weight:600; text-align:center; }
            h2 { margin:0 0 12px 0; }
          </style>
        </head>
        <body>
          <h2>SOB FY Report - ${financialYear || "All"}</h2>
          <div style="margin-bottom:8px;">Filters: State=${selectedState}, Division=${selectedDivision}, Employee=${selectedEmployee}</div>
          <table>
            <thead>
              <tr>${headerCols.map((c) => `<th>${c}</th>`).join("")}</tr>
            </thead>
            <tbody>
              ${rowsHtml}
              ${footerRow}
            </tbody>
          </table>
          <div style="margin-top:12px;font-size:12px">Generated on: ${new Date().toLocaleString()}</div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      try {
        printWindow.print();
      } catch (e) {
        console.error("Print error", e);
      }
    }, 400);
  };

 const handleExport = () => {
    if (!tableData || !tableData.length) {
      toast.error("No data to export");
      return;
    }

    try {
      const wb = XLSX.utils.book_new();
      const ws = {};

      const headers = [
        "S.No", "Emp ID", "State", "Employee", "Designation", "HQ", "Division",
        "DOJ", "DOR", "Manager", "Phone No",
        ...monthsDisplay.map((m) => `${m} SOB`),
        "Total",
      ];

      const headerStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
        fill: { fgColor: { rgb: "2563EB" } },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };

      const rowStyle = {
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };

      const footerStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
        fill: { fgColor: { rgb: "2563EB" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };

      // ✅ NEW: real Excel date cell setter
      const setDateCell = (r, c, rawVal, s) => {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (rawVal) {
          try {
            const date = new Date(
              /^\d{4}-\d{2}-\d{2}$/.test(String(rawVal))
                ? `${rawVal}T00:00:00`
                : rawVal
            );
            if (!isNaN(date.getTime())) {
              ws[addr] = { v: date, t: "d", z: "dd-mmm-yyyy", s };
              return;
            }
          } catch {}
        }
        ws[addr] = { v: "", t: "s", s };
      };

      // Header row
      headers.forEach((header, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex });
        ws[cellAddress] = { v: header, t: "s", s: headerStyle };
      });

      // Data rows
      tableData.forEach((row, index) => {
        const excelRow = index + 2;

        const empRaw = String(row.employee ?? "").trim();
        const empIsNum = /^\d+$/.test(empRaw);
        const empID = empIsNum ? Number(empRaw) : empRaw;

        const rowData = [
          index + 1,
          empID,
          row.state || "",
          row.employee_name || "",
          row.designation || "",
          row.hq || "",
          row.division || "",
          null, // col 7 — DOJ (patched below)
          null, // col 8 — DOR (patched below)
          row.manager || "",
          row.phone_no || "",
          Number(row.apr_sob || 0),
          Number(row.may_sob || 0),
          Number(row.jun_sob || 0),
          Number(row.jul_sob || 0),
          Number(row.aug_sob || 0),
          Number(row.sep_sob || 0),
          Number(row.oct_sob || 0),
          Number(row.nov_sob || 0),
          Number(row.dec_sob || 0),
          Number(row.jan_sob || 0),
          Number(row.feb_sob || 0),
          Number(row.mar_sob || 0),
          0,
        ];

        rowData.forEach((value, colIndex) => {
          // ✅ Skip date cols — patched below
          if (colIndex === 7 || colIndex === 8) return;

          const cellAddress = XLSX.utils.encode_cell({ r: index + 1, c: colIndex });
          ws[cellAddress] = {
            v: value,
            t:
              colIndex === 1
                ? empIsNum ? "n" : "s"
                : typeof value === "number" ? "n" : "s",
            s: rowStyle,
          };
        });

        // ✅ Patch DOJ and DOR as real Excel dates
        setDateCell(index + 1, 7, row.final_joining_date, rowStyle);
        setDateCell(index + 1, 8, row.resign_date, rowStyle);

        // Total formula per data row
        ws[`X${excelRow}`] = { f: `SUM(L${excelRow}:W${excelRow})`, t: "n", s: rowStyle };
      });

      // Footer totals row
      const footerRowIndex = tableData.length + 1;

      for (let c = 0; c <= 10; c++) {
        const cellAddress = XLSX.utils.encode_cell({ r: footerRowIndex, c });
        ws[cellAddress] = {
          v: c === 0 ? "Total" : "",
          t: "s",
          s: footerStyle,
        };
      }

      columnTotals.forEach((total, i) => {
        const cellAddress = XLSX.utils.encode_cell({ r: footerRowIndex, c: 11 + i });
        ws[cellAddress] = { v: Math.round(total), t: "n", s: footerStyle };
      });

      ws[XLSX.utils.encode_cell({ r: footerRowIndex, c: 23 })] = {
        v: Math.round(grandTotal),
        t: "n",
        s: footerStyle,
      };

      // Sheet range (includes footer row)
      ws["!ref"] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: footerRowIndex, c: headers.length - 1 },
      });

      ws["!cols"] = [
        { wch: 8 }, { wch: 14 }, { wch: 14 }, { wch: 28 }, { wch: 18 },
        { wch: 18 }, { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 24 }, { wch: 16 },
        ...Array.from({ length: 13 }, () => ({ wch: 12 })),
      ];

      ws["!freeze"] = { xSplit: 0, ySplit: 1 };
      ws["!autofilter"] = { ref: "A1:X1" };

      XLSX.utils.book_append_sheet(wb, ws, "SOB FY Report");

      const safe = (s) => String(s || "").replace(/[^a-z0-9_\-]/gi, "_");
      const fileName = `SOB_FY_Report_${safe(financialYear)}_${new Date().toISOString().slice(0, 10)}.xlsx`;

      XLSX.writeFile(wb, fileName, { compression: true });
    } catch (err) {
      toast.error("Failed to export Excel");
    }
  };
  return (
    <div className="p-2 border-2 border-gray-300 rounded-lg bg-white relative">
      {isLoading && <Loader onCancel={() => setIsLoading(false)} />}

      {/* Filter container */}
      <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">SOB</span>{" "}
          <span className="text-blue-500">FY</span>
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
          <label className="text-right text-sm text-gray-700">State</label>
          <select
            className={`w-full mt-1 border rounded px-2 py-1 text-sm`}
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
            disabled={isLoading}
          >
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>

          <label className="text-right text-sm text-gray-700">Division</label>
          <select
            className="w-full mt-1 border rounded px-2 py-1 text-sm"
            value={selectedDivision}
            onChange={(e) => handleDivisionChange(e.target.value)}
            disabled={isLoading}
          >
            {divisions.map((division) => (
              <option key={division.id} value={division.id}>
                {division.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center mt-4">
          <label className="text-right text-sm text-gray-700">Employee</label>
          <select
            className="w-full mt-1 border rounded px-2 py-1 text-sm"
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            disabled={isLoading}
          >
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.id === "All"
                  ? "All"
                  : `${emp.first_name} ${emp.last_name} | ${emp.employee}  | ${emp.designation || "-"} | ${emp.division || "-"} | ${emp.state_name || "-"}`}
              </option>
            ))}
          </select>

          <label className="text-right text-sm text-gray-700">
            Financial Year
          </label>
          <select
            className="w-full mt-1 border rounded px-2 py-1 text-sm"
            value={financialYear}
            onChange={(e) => setFinancialYear(e.target.value)}
            disabled={isLoading}
          >
            {financialYearOptions.map((fy, idx) => (
              <option key={idx} value={fy}>
                {fy}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-center mt-4">
          <CustomButton
            variant="go"
            onClick={handleGoClick}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "GO"}
          </CustomButton>
        </div>

        {error && <div className="mt-3 text-center text-red-600">{error}</div>}
      </div>

      {showContainers && (
        <>
          <div className="border rounded shadow table-wrapper mt-5">
            <table className="unlock-dcr-leave-table text-sm">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="px-3 py-2 border text-center text-xs">S.No</th>
                  <th className="px-3 py-2 border text-center text-xs">Emp ID</th>
                  <th className="px-3 py-2 border text-center text-xs">State</th>
                  <th className="px-3 py-2 border text-left text-xs">Employee</th>
                  <th className="px-3 py-2 border text-center text-xs">Designation</th>
                  <th className="px-3 py-2 border text-center text-xs">HQ</th>
                  <th className="px-3 py-2 border text-center text-xs">Division</th>
                  <th className="px-3 py-2 border text-center text-xs">DOJ</th>
                  <th className="px-3 py-2 border text-center text-xs">DOR</th>
                  <th className="px-3 py-2 border text-center text-xs">Manager</th>
                  <th className="px-3 py-2 border text-center text-xs">Phone No</th>
                  {monthsDisplay.map((m) => (
                    <th key={`${m}-sob`} className="px-3 py-2 border text-center text-xs">
                      {m} SOB
                    </th>
                  ))}
                  <th className="px-3 py-2 border text-center text-xs">Total</th>
                </tr>
              </thead>

              <tbody className="bg-white">
                {tableData && tableData.length > 0 ? (
                  tableData.map((row, index) => {
                    const runningTotal = calculateRunningTotal(row);
                    return (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-3 py-2 border text-center align-middle text-xs">{index + 1}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.employee}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.state}</td>
                        <td className="px-3 py-2 border text-left align-middle text-xs">{row.employee_name}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.designation}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.hq}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.division}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{formatDate(row.final_joining_date) || "-"}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{formatDate(row.resign_date) || "-"}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.manager || "-"}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.phone_no || "-"}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.apr_sob || 0}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.may_sob || 0}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.jun_sob || 0}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.jul_sob || 0}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.aug_sob || 0}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.sep_sob || 0}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.oct_sob || 0}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.nov_sob || 0}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.dec_sob || 0}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.jan_sob || 0}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.feb_sob || 0}</td>
                        <td className="px-3 py-2 border text-center align-middle text-xs">{row.mar_sob || 0}</td>
                       <td className="px-3 py-2 border text-center align-middle text-xs">
                              {Math.round(runningTotal)}
                            </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={24} className="px-4 py-6 text-center text-gray-500 text-sm">
                      No data found.
                    </td>
                  </tr>
                )}
              </tbody>

              {/* Footer totals row */}
              {tableData && tableData.length > 0 && (
                <tfoot>
                  <tr className="bg-blue-600 text-white font-bold">
                    <td colSpan={11} className="px-3 py-2 border text-center text-xs">
                      Total
                    </td>
                    {columnTotals.map((total, i) => (
                      <td key={i} className="px-3 py-2 border text-center text-xs">
                        {Math.round(total)}
                      </td>
                    ))}
                    <td className="px-3 py-2 border text-center text-xs">
                      {Math.round(grandTotal)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="mt-4 flex gap-4 justify-center">
            <CustomButton onClick={handlePrint} label="Print" variant="print">
              🖨
            </CustomButton>
            <CustomButton onClick={handleExport} label="Export" variant="export">
              📥
            </CustomButton>
          </div>
        </>
      )}
    </div>
  );
};

export default SobFY;